import { useTranslation } from '@pancakeswap/localization'
import {
  AddIcon,
  Flex,
  IconButton,
  MinusIcon,
  Text,
} from '@pancakeswap/uikit'
import { NumericalInput } from '@pancakeswap/widgets-internal'
import { FeeAmount } from '@pancakeswap/v3-sdk'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { Price, Token, Currency } from '@pancakeswap/swap-sdk-core'
import { tryParsePrice } from 'hooks/v3/utils'
import styled from 'styled-components'

const StepBox = styled(Flex)`
  border: 1px solid #F1D1E329;
  background: #0000004D;
  border-radius: 12px;
  // flex-direction: column;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const InputWrapper = styled(Flex)`
  width: 100%;
`

const StyledIconButton = styled(IconButton)`
  &:disabled {
    background: transparent;
    opacity: 0.1;
  }

  @media (max-width: 768px) {
    padding: 4px;
  }
`

const ButtonWrapper = styled(Flex)<{locked: boolean}>`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  flex-direction: column;

  ${({locked}) => !locked && `
    border-width: 0 1px;
    border-color: #F1D1E329;
    border-style: solid;
  `}
  
  @media (max-width: 768px) {
    padding: 4px;
  }
`

const TitleText = styled(Text)`
  font-size: 12px;
  text-align: center;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`

const ButtonGroup = styled(Flex)`
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`

interface StepCounterProps {
  value: string
  onUserInput: (value: string | undefined) => void
  decrement: () => Price<Token, Token> | undefined
  increment: () => Price<Token, Token> | undefined
  decrementDisabled?: boolean
  incrementDisabled?: boolean
  feeAmount?: FeeAmount
  label?: string
  width?: string
  locked?: boolean // disable input
  title: ReactNode
  tokenA: Currency | undefined
  tokenB: Currency | undefined
}

const StepCounter = ({
  value,
  decrement,
  increment,
  decrementDisabled = false,
  incrementDisabled = false,
  locked,
  onUserInput,
  title,
  label,
  tokenA,
  tokenB,
}: StepCounterProps) => {
  const { t } = useTranslation()
  const [, setActive] = useState(false)
  const [localValue, setLocalValue] = useState('')
  const [useLocalValue, setUseLocalValue] = useState(false)

  const handleOnFocus = useCallback(() => {
    setUseLocalValue(true)
    setActive(true)
  }, [])

  const handleOnBlur = useCallback(() => {
    setUseLocalValue(false)
    setActive(false)
    onUserInput(localValue) // trigger update on parent value
  }, [localValue, onUserInput])

  const handleDecrement = useCallback(() => {
    setUseLocalValue(false)
    onUserInput(decrement()?.toSignificant(8) ?? localValue)
  }, [decrement, onUserInput, localValue])

  const handleIncrement = useCallback(() => {
    setUseLocalValue(false)
    onUserInput(increment()?.toSignificant(8) ?? localValue)
  }, [increment, onUserInput, localValue])

  useEffect(() => {
    if (localValue !== value && !useLocalValue) {
      setLocalValue(value)
    }
  }, [localValue, useLocalValue, value])

  return (
    <StepBox>
      {!locked && (
        <StyledIconButton
        onClick={handleDecrement}
        disabled={decrementDisabled}
        scale="sm"
        variant="text"
        >
          <MinusIcon color="textSubtle" width={24} height={24} />
        </StyledIconButton>
      )}
      <ButtonWrapper locked>
        <TitleText color="textSubtle">
          {title} ({label})
        </TitleText>
        <InputWrapper onFocus={handleOnFocus} onBlur={handleOnBlur}>
          <NumericalInput
            style={{ minWidth: "140px", textAlign: "center" }}
            value={localValue}
            fontSize="16px"
            align="center"
            disabled={locked}
            onUserInput={setLocalValue}
          />
        </InputWrapper>
        {/* <TitleText color="textSubtle">
          {label}
        </TitleText> */}
      </ButtonWrapper>
      {!locked && (
        <StyledIconButton
          onClick={handleIncrement}
          disabled={incrementDisabled}
          scale="sm"
          variant="text"
        >
          <AddIcon color="textSubtle" width={24} height={24} />
        </StyledIconButton>
      )}
    </StepBox>
  )
}

export default StepCounter
